import { Form } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import FileDisplay from "go-component/components/ImportCsv/components/FileDisplay";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import { InvoiceDetailsFormType } from "../../../../../../../../../../../../services/Api/Organization/types";
import { KSeFFileInputNamePairs, KSeFFileInputNames } from "../../utils/types";

interface Props {
	form: UseFormReturn<InvoiceDetailsFormType>;
	fileInputNames: KSeFFileInputNamePairs;
	filePasswordInputName: "ksef_auth_cert_password" | "ksef_offline_cert_password";
	filePasswordLabel: string;
}

export const KSeFCertificateInputs = ({ form, fileInputNames, filePasswordInputName, filePasswordLabel }: Props) => {
	const {
		formState: { errors },
		register,
		clearErrors,
		setValue,
		getFieldState,
		watch,
		setError,
	} = form;
	const { t } = useTranslation();
	const getFileExtensionName = (inputName: KSeFFileInputNames) => (inputName.includes("crt") ? "crt" : "key");
	const wrapperTestId = `wrapper-${fileInputNames[0].includes("auth") ? "auth" : "offline"}`;
	const errorFieldName = fileInputNames[0].includes("auth")
		? "ksef_crt_auth_cert_file"
		: "ksef_crt_offline_cert_file";
	const invalidFileError = getFieldState(errorFieldName).error?.message;

	const fileUploadHandler = (inputName: KSeFFileInputNames, fileObj?: File) => {
		const properFileExtension = inputName.includes("crt") ? "crt" : "key";
		const fileExtension = fileObj?.name.split(".").pop();

		if (fileExtension !== properFileExtension) {
			setError(errorFieldName, {
				message: t("modules.invoice_details.field.invalid_file_extension.title", {
					extension: properFileExtension,
				}),
			});
			return;
		}

		clearErrors(errorFieldName);

		setValue(inputName, fileObj);
		clearErrors(fileInputNames);
	};

	return (
		<div data-testid={wrapperTestId}>
			{fileInputNames.map((KSeFInputType) => {
				const watchedFile: File | undefined = watch(KSeFInputType);

				return (
					<Form.Group className="form-group mt-1" key={KSeFInputType}>
						<Form.Text>
							{t(`modules.invoice_details.field.file_with_extension.title`, {
								extension: getFileExtensionName(KSeFInputType),
							})}
						</Form.Text>
						<div className="d-flex flex-column align-items-center justify-content-center">
							<FileUpload
								onUpdateFile={(_: string, fileObj?: File) => fileUploadHandler(KSeFInputType, fileObj)}
								fileIcon={
									watchedFile ? (
										<FileDisplay icon={<EmptyImageSVG />} fileName={watchedFile.name} />
									) : undefined
								}
								invalidExtensionCustomMessage={t(
									"modules.invoice_details.field.invalid_file_extension.title",
									{ extension: getFileExtensionName(KSeFInputType) }
								)}
							/>
						</div>
					</Form.Group>
				);
			})}
			<FormInput
				name={filePasswordInputName}
				errors={errors}
				register={register}
				label={filePasswordLabel}
				type="password"
				autoComplete="new-password"
				onChange={() => clearErrors(fileInputNames)}
			/>
			{invalidFileError && <Form.Text className="text-danger align-self-start">{invalidFileError}</Form.Text>}
		</div>
	);
};
